import { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ctaApi } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * Sponsor Section Component
 * Displays sponsor branding and configurable CTAs on artifact modules
 * 
 * @param {Object} sponsorInfo - The sponsor info from tokenApi.getSponsorInfo()
 * @param {string} tokenId - The current token ID (for analytics)
 * @param {string} moduleType - The current module type (notes, travel, home)
 * @param {string} className - Additional CSS classes
 */
export default function SponsorSection({ sponsorInfo, tokenId, moduleType, className }) {
  const [clickingCta, setClickingCta] = useState(null);

  // Don't render if no sponsor
  if (!sponsorInfo?.has_sponsor) {
    return null;
  }

  const { sponsor, campaign, ctas, branding } = sponsorInfo;
  const activeCtas = ctas?.filter(c => c.is_active) || [];

  const handleCtaClick = async (cta) => {
    setClickingCta(cta.id);
    try {
      const response = await ctaApi.recordClick(cta.id, tokenId, moduleType);
      if (cta.open_in_new_tab) {
        window.open(cta.link, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = cta.link;
      }
    } catch (error) {
      if (cta.open_in_new_tab) {
        window.open(cta.link, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = cta.link;
      }
    } finally {
      setClickingCta(null);
    }
  };

  const headerColor = branding?.header_color;
  const headerText = branding?.header_text || campaign?.name || 'Sponsor';
  const headerLogo = branding?.header_logo_base64;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={cn(
        "rounded-2xl border border-slate-200 p-6",
        !headerColor && "bg-gradient-to-br from-slate-50 to-slate-100",
        className
      )}
      style={headerColor ? { background: `linear-gradient(135deg, ${headerColor}10, ${headerColor}20)`, borderColor: `${headerColor}30` } : undefined}
      data-testid="sponsor-section"
    >
      {/* Sponsor Header */}
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm border border-slate-100 overflow-hidden">
          {headerLogo ? (
            <img src={`data:image/png;base64,${headerLogo}`} alt="Logo" className="w-full h-full object-cover" data-testid="sponsor-logo" />
          ) : (
            <Building2 className="w-6 h-6 text-slate-600" />
          )}
        </div>
        <div>
          <h4 className="font-semibold text-slate-900" style={headerColor ? { color: headerColor } : undefined}>{headerText}</h4>
          <p className="text-sm text-slate-500">
            {sponsor?.company_name && (
              <>Powered by <span className="font-medium">{sponsor.company_name}</span></>
            )}
          </p>
        </div>
      </div>

      {/* CTA Buttons */}
      {activeCtas.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {activeCtas.map((cta, index) => (
            <Button
              key={cta.id}
              onClick={() => handleCtaClick(cta)}
              disabled={clickingCta === cta.id}
              className={cn(
                "rounded-full font-medium transition-all duration-200",
                index === 0 
                  ? "bg-slate-900 hover:bg-slate-800 text-white" 
                  : "bg-white hover:bg-slate-50 text-slate-900 border border-slate-200"
              )}
              data-testid={`sponsor-cta-${cta.id}`}
            >
              {cta.title}
              {cta.open_in_new_tab && <ExternalLink className="w-4 h-4 ml-2" />}
            </Button>
          ))}
        </div>
      )}

      {/* Branding footer */}
      <p className="text-xs text-slate-400 mt-4">
        Powered by MemoraOrbs
      </p>
    </motion.div>
  );
}
